/**
 * 将设计师字体分类接口转换为 Canvas 字体目录及字体面板共用的素材结构
 * 隔离供稿接口的字符串资源字段与 xdesign-widgets 的资源对象格式
 */
import type { FontMaterialRaw } from '@meitu/xdesign-widgets';

type Resource = string | { url: string } | [] | null;
type ResourceKey =
    | 'font_file'
    | 'woff2_file'
    | 'zip_file'
    | 'thumbnail_text_dark'
    | 'thumbnail_text_white'
    | 'corner_mark';

/** 设计师接口字体素材，资源允许返回 URL 字符串或空数组 */
export type DesignerFontMaterial = Omit<Partial<FontMaterialRaw>, ResourceKey> &
    Partial<Record<ResourceKey, Resource>> & {
        id: number;
        font_name: string;
        name?: string;
        zip_url?: string;
    };

const resourceUrl = (resource?: Resource): string =>
    typeof resource === 'string' ? resource : resource && !Array.isArray(resource) ? resource.url : '';

/**
 * 归一化设计师字体资源，保留字体族、分类及度量信息
 * @param material - 字体接口原始素材
 * @param category - 素材所属分类
 * @returns 新引擎可消费的字体素材
 */
export const normalizeFontMaterial = (
    material: DesignerFontMaterial,
    category: { category_id: number; category_name: string },
): FontMaterialRaw => ({
    type: 'fonts',
    corner_mark_id: 0,
    extra: '',
    status: 1,
    threshold: 0,
    thumbnail: { url: '', width: 0, height: 0 },
    type_id: 0,
    font_group_name: '',
    font_weight_value: 0,
    font_metrics: '',
    font_group_thumbnail_dark: { url: '' },
    font_group_thumbnail_white: { url: '' },
    ...material,
    ...category,
    default_name: material.default_name || material.name || material.font_name,
    font_file: { url: resourceUrl(material.font_file) },
    woff2_file: { url: resourceUrl(material.woff2_file) },
    zip_file: { url: resourceUrl(material.zip_file) || material.zip_url || '' },
    thumbnail_text_dark: { url: resourceUrl(material.thumbnail_text_dark) },
    thumbnail_text_white: { url: resourceUrl(material.thumbnail_text_white) },
    corner_mark: { url: resourceUrl(material.corner_mark), width: 0, height: 0 },
});
